import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useUIStore } from '../../stores/ui';
import { authApi } from '../../api/auth';
import styles from './Sidebar.module.css';

interface NavChild {
  to: string;
  label: string;
  comingSoon?: boolean;
}

interface NavItem {
  to: string;
  label: string;
  icon: string;
  exact?: boolean;
  children?: NavChild[];
  comingSoon?: boolean;
}

const NAV: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: '⊞', exact: true },
  { to: '/tenants', label: 'Tenants', icon: '🏢' },
  {
    to: '/identity', label: 'Identity', icon: '👤',
    children: [
      { to: '/identity/users', label: 'Users' },
      { to: '/identity/groups', label: 'Groups' },
      { to: '/identity/devices', label: 'Devices' },
      { to: '/identity/roles', label: 'Roles' },
      { to: '/identity/conditional-access', label: 'Conditional Access' },
      { to: '/identity/risky-users', label: 'Risky Users' },
    ],
  },
  {
    to: '/security', label: 'Security', icon: '🔒',
    children: [
      { to: '/security/secure-score', label: 'Secure Score' },
      { to: '/security/mfa', label: 'MFA Status' },
      { to: '/security/defender', label: 'Defender' },
      { to: '/security/compliance', label: 'Compliance', comingSoon: true },
    ],
  },
  {
    to: '/intune', label: 'Intune', icon: '📱',
    children: [
      { to: '/intune/devices', label: 'Devices' },
      { to: '/intune/profiles', label: 'Config Profiles' },
      { to: '/intune/compliance', label: 'Compliance Policies' },
      { to: '/intune/apps', label: 'App Deployment' },
      { to: '/intune/scripts', label: 'Scripts' },
    ],
  },
  {
    to: '/exchange', label: 'Exchange & Email', icon: '✉️',
    children: [
      { to: '/exchange/mailboxes', label: 'Mailboxes' },
      { to: '/exchange/shared', label: 'Shared Mailboxes' },
      { to: '/exchange/distribution', label: 'Distribution Groups' },
      { to: '/exchange/mail-flow', label: 'Mail Flow' },
      { to: '/exchange/spam', label: 'Spam Policies', comingSoon: true },
      { to: '/exchange/mail-hardening', label: 'Mail Hardening' },
      { to: '/exchange/message-trace', label: 'Message Trace' },
      { to: '/exchange/remediation', label: 'Remediation' },
      { to: '/exchange/journaling', label: 'Journaling' },
    ],
  },
  {
    to: '/teams', label: 'Teams & SharePoint', icon: '💬',
    children: [
      { to: '/teams/policies', label: 'Teams Policies', comingSoon: true },
      { to: '/teams/sharepoint', label: 'SharePoint', comingSoon: true },
      { to: '/teams/onedrive', label: 'OneDrive', comingSoon: true },
    ],
  },
  {
    to: '/unifi', label: 'UniFi Network', icon: '📡',
    children: [
      { to: '/unifi/sites', label: 'Sites' },
      { to: '/unifi/devices', label: 'Devices' },
      { to: '/unifi/clients', label: 'Clients' },
      { to: '/unifi/networks', label: 'Networks & VLANs' },
      { to: '/unifi/traffic', label: 'Traffic' },
      { to: '/unifi/cloud/hosts', label: 'Cloud: Hosts' },
      { to: '/unifi/cloud/sites', label: 'Cloud: Sites' },
      { to: '/unifi/cloud/devices', label: 'Cloud: Devices' },
      { to: '/unifi/cloud/isp-metrics', label: 'Cloud: ISP Metrics' },
      { to: '/unifi/cloud/sd-wan', label: 'Cloud: SD-WAN' },
      { to: '/unifi/cloud/keys', label: 'Cloud: API Keys' },
      { to: '/unifi/reports', label: 'Reports' },
    ],
  },
  {
    to: '/ai', label: 'AI Assistant', icon: '✨',
    children: [
      { to: '/ai/chat', label: 'Chat' },
      { to: '/ai/configurations', label: 'Configurations' },
      { to: '/ai/history', label: 'History' },
    ],
  },
  {
    to: '/tools', label: 'Tools', icon: '🔧',
    children: [
      { to: '/tools/templates', label: 'Template Library' },
      { to: '/tools/scheduler', label: 'Scheduler' },
      { to: '/tools/reports', label: 'Report Builder' },
      { to: '/tools/domain', label: 'Domain Tools' },
    ],
  },
  { to: '/webhooks', label: 'Webhooks', icon: '🔔' },
  {
    to: '/integrations', label: 'Integrations', icon: '🔗',
    children: [
      { to: '/integrations/enque', label: 'Enque Ticketing' },
    ],
  },
  { to: '/agencies', label: 'Agencies', icon: '🏛️' },
  { to: '/settings', label: 'Settings', icon: '⚙️' },
];

function ChildLink({ child }: { child: NavChild }) {
  return (
    <NavLink
      to={child.to}
      className={({ isActive }) => [
        styles.childLink,
        isActive ? styles.active : '',
        child.comingSoon ? styles.comingSoon : '',
      ].join(' ')}
    >
      <span className={styles.label}>{child.label}</span>
      {child.comingSoon && <span className={styles.soonBadge}>soon</span>}
    </NavLink>
  );
}

function NavItemComponent({ item }: { item: NavItem }) {
  const location = useLocation();
  const isParentActive = location.pathname.startsWith(item.to) && item.to !== '/dashboard';
  const collapsed = useUIStore(s => s.sidebarCollapsed);
  const [open, setOpen] = useState(isParentActive);

  if (item.children && !collapsed) {
    return (
      <div className={styles.group}>
        <button
          className={[styles.groupLabel, isParentActive ? styles.groupLabelActive : ''].join(' ')}
          onClick={() => setOpen(o => !o)}
          aria-expanded={open}
        >
          <span className={styles.icon}>{item.icon}</span>
          <span className={styles.label}>{item.label}</span>
          <svg
            className={[styles.chevron, open ? styles.chevronOpen : ''].join(' ')}
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            aria-hidden="true"
          >
            <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <div className={[styles.children, open ? styles.childrenOpen : ''].join(' ')}>
          {item.children.map(child => <ChildLink key={child.to} child={child} />)}
        </div>
      </div>
    );
  }

  return (
    <NavLink
      to={item.to}
      end={item.exact ?? false}
      className={({ isActive }) => [
        styles.navLink,
        isActive || isParentActive ? styles.active : '',
        item.comingSoon ? styles.comingSoon : '',
      ].join(' ')}
      title={collapsed ? item.label : undefined}
    >
      <span className={styles.icon}>{item.icon}</span>
      {!collapsed && <span className={styles.label}>{item.label}</span>}
      {!collapsed && item.comingSoon && <span className={styles.soonBadge}>soon</span>}
    </NavLink>
  );
}

async function handleSignOut() {
  try {
    await authApi.logout();
  } catch {
    // ignore — we redirect regardless
  }
  window.location.href = '/auth/login';
}

export function Sidebar() {
  const { sidebarCollapsed, toggleSidebar } = useUIStore();

  return (
    <aside className={[styles.sidebar, sidebarCollapsed ? styles.collapsed : ''].join(' ')}>
      <div className={styles.header}>
        {!sidebarCollapsed && (
          <span className={styles.logo}>
            <span className={styles.logoMark}>NZT</span>
          </span>
        )}
        <button className={styles.collapseBtn} onClick={toggleSidebar} title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
          {sidebarCollapsed ? '→' : '←'}
        </button>
      </div>

      <nav className={styles.nav}>
        {NAV.map(item => (
          <NavItemComponent key={item.to} item={item} />
        ))}
      </nav>

      <div className={styles.footer}>
        {!sidebarCollapsed && (
          <>
            <button className={styles.signOutBtn} onClick={() => void handleSignOut()} title="Sign out">
              Sign out
            </button>
            <span className={styles.version}>v0.1.0</span>
          </>
        )}
      </div>
    </aside>
  );
}
